import { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { Modal } from 'react-bootstrap-v5';
import Swal from 'sweetalert2';
import { INfseNota, INfseVisao, IRetornoProvisaoOpcao } from '../../interfaces/IModuloBancario';
import ArquivoOperacaoService from '../../services/ArquivoOperacaoService';
import ModuloBancarioService from '../../services/ModuloBancarioService';
import { BarraFiltros, BuscaOpcoes, PaginacaoModulo, useFiltroLista } from './paginacao-modulo';

interface Props {
    esteiraId: number;
    /** "AAAA-MM"; vazio = nenhuma competência escolhida. */
    competencia: string;
    /** A geração das minutas muda a situação das notas. */
    versaoMinutas: number;
    /** Ler notas ou vincular uma provisão muda as minutas. */
    onAlterou: () => void;
}

const INTERVALO_MS = 4000;

const SITUACAO_ARQUIVO: Record<string, { texto: string; classe: string }> = {
    ANALISANDO: { texto: 'Lendo', classe: 'badge-light-primary' },
    LIDO: { texto: 'Lido', classe: 'badge-light-success' },
    DUPLICADO: { texto: 'Já enviado', classe: 'badge-light' },
    ERRO: { texto: 'Erro', classe: 'badge-light-danger' },
};

const STATUS: Record<string, { texto: string; classe: string }> = {
    RECUSADA: { texto: 'Recusada', classe: 'badge-light-danger' },
    CANCELADA: { texto: 'Cancelada', classe: 'badge-light' },
    SEM_RETENCAO: { texto: 'Sem retenção', classe: 'badge-light' },
    SEM_MOVIMENTO: { texto: 'Sem movimento no extrato', classe: 'badge-light-warning' },
    PENDENTE: { texto: 'Pendente', classe: 'badge-light-warning' },
    EM_MINUTA: { texto: 'Em minuta', classe: 'badge-light-primary' },
    LIQUIDADA: { texto: 'Liquidada', classe: 'badge-light-success' },
};

const brl = (valor: number) => valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const VAZIO: INfseVisao = { arquivos: [], notas: [], totalItens: 0, pagina: 1, tamanhoPagina: 50 };
const STATUS_FILTRO = Object.fromEntries(Object.entries(STATUS).map(([k, v]) => [k, v.texto]));

// DEVN3-776 — XMLs de NFS-e: leitura sem custo, retenções por nota e vínculo manual da provisão.
export default function NotasFiscais({ esteiraId, competencia, versaoMinutas, onAlterou }: Props) {
    const [visao, setVisao] = useState<INfseVisao>(VAZIO);
    const [enviando, setEnviando] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [vinculo, setVinculo] = useState<{ nota: INfseNota; opcoes: IRetornoProvisaoOpcao[]; total: number; escolhida: string } | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const [ano, mes] = competencia ? competencia.split('-').map(Number) : [0, 0];
    const filtro = useFiltroLista();

    const buscar = useCallback(async (silencioso = false) => {
        if (!ano || !mes) return;
        try {
            setVisao(await ModuloBancarioService.nfse(esteiraId, ano, mes, filtro.params));
        } catch (err: any) {
            if (!silencioso) Swal.fire({ text: err?.message ?? 'Não foi possível carregar as notas.', icon: 'error' });
        }
    }, [esteiraId, ano, mes, filtro.params]);

    // O filtro muda a consulta, não a competência: a lista antiga fica na tela até a nova chegar.
    useEffect(() => {
        setVisao(VAZIO);
    }, [esteiraId, ano, mes]);

    useEffect(() => {
        buscar();
    }, [buscar]);

    useEffect(() => {
        if (versaoMinutas > 0) buscar(true);
    }, [versaoMinutas, buscar]);

    const emAndamento = visao.arquivos.some(a => a.situacao === 'ANALISANDO');
    useEffect(() => {
        if (!emAndamento) return;
        const id = setInterval(() => buscar(true), INTERVALO_MS);
        return () => clearInterval(id);
    }, [emAndamento, buscar]);

    // Terminada a leitura, a geração refaz as minutas: a lista precisa acompanhar.
    const processavaAntes = useRef(false);
    useEffect(() => {
        if (processavaAntes.current && !emAndamento) onAlterou();
        processavaAntes.current = emAndamento;
    }, [emAndamento, onAlterou]);

    const enviar = async (ev: React.ChangeEvent<HTMLInputElement>) => {
        const files = ev.target.files;
        if (!files || files.length === 0) return;

        setEnviando(true);
        const falhas: string[] = [];
        for (const file of Array.from(files)) {
            try {
                const { signedUrl, generatedFileName } = await ArquivoOperacaoService.gerarUrl(file.name, file.type || 'application/octet-stream');
                await ArquivoOperacaoService.enviarParaStorage(signedUrl, file);
                await ModuloBancarioService.registrarNfse(esteiraId, { ano, mes, nomeGerado: generatedFileName, nomeOriginal: file.name });
            } catch (err: any) {
                falhas.push(`${file.name}: ${err?.message ?? 'falha no envio'}`);
            }
        }
        setEnviando(false);
        if (inputRef.current) inputRef.current.value = '';
        if (falhas.length > 0) Swal.fire({ title: 'Alguns arquivos não foram enviados', html: falhas.join('<br/>'), icon: 'warning' });
        buscar();
    };

    const executar = async (acao: () => Promise<unknown>, erro: string) => {
        setSalvando(true);
        try {
            await acao();
            await buscar(true);
            return true;
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? erro, icon: 'error' });
            return false;
        } finally {
            setSalvando(false);
        }
    };

    const abrirVinculo = async (nota: INfseNota) => {
        try {
            const { itens, total } = await ModuloBancarioService.provisoesDaNota(esteiraId, nota.id);
            setVinculo({ nota, opcoes: itens, total, escolhida: nota.lancamentoId ? String(nota.lancamentoId) : '' });
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? 'Não foi possível carregar as provisões.', icon: 'error' });
        }
    };

    const notaDoVinculo = vinculo?.nota.id;
    const buscarProvisoes = useCallback(async (busca: string) => {
        if (!notaDoVinculo) return;
        try {
            const { itens, total } = await ModuloBancarioService.provisoesDaNota(esteiraId, notaDoVinculo, busca);
            setVinculo(v => (v ? { ...v, opcoes: itens, total } : v));
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? 'Não foi possível buscar as provisões.', icon: 'error' });
        }
    }, [esteiraId, notaDoVinculo]);

    const salvarVinculo = async () => {
        if (!vinculo) return;
        const ok = await executar(
            () => ModuloBancarioService.vincularNota(esteiraId, vinculo.nota.id, vinculo.escolhida ? Number(vinculo.escolhida) : null),
            'Não foi possível vincular a provisão.');
        if (ok) {
            setVinculo(null);
            onAlterou();
        }
    };

    const nomeArquivo = (id: number) => visao.arquivos.find(a => a.id === id)?.nomeOriginal ?? 'nota.xml';
    const podeVincular = (n: INfseNota) => n.situacao === 'SEM_MOVIMENTO' || n.situacao === 'PENDENTE' || n.situacao === 'EM_MINUTA';

    return (
        <div className='card card-flush mb-4'>
            <div className='card-header align-items-center'>
                <h3 className='card-title fw-bold'>
                    Notas fiscais de serviço
                    {(enviando || emAndamento) && <span className='spinner-border spinner-border-sm ms-3'></span>}
                </h3>
                {competencia && (
                    <div className='d-flex align-items-center gap-2'>
                        <input ref={inputRef} type='file' multiple accept='.xml,.zip' className='d-none' onChange={enviar} />
                        <button className='btn btn-sm btn-light-primary' disabled={enviando} onClick={() => inputRef.current?.click()}>
                            <i className='fas fa-upload'></i> Enviar XMLs
                        </button>
                    </div>
                )}
            </div>
            <div className='card-body pt-0'>
                {!competencia ? (
                    <div className='text-muted'>Escolha a competência no topo da página.</div>
                ) : (
                    <>
                        <div className='text-muted fs-7 mb-3'>
                            XML de NFS-e, do padrão nacional ou ABRASF, ou um .zip com vários. A nota explica a diferença entre o líquido do extrato e o bruto da provisão: a retenção vai para a conta dela, nunca para o banco.
                        </div>

                        {visao.arquivos.map(a => (
                            <div key={a.id} className='d-flex flex-wrap align-items-center gap-2 fs-7 mb-2'>
                                <span className={`badge ${SITUACAO_ARQUIVO[a.situacao]?.classe ?? 'badge-light'}`}>{SITUACAO_ARQUIVO[a.situacao]?.texto ?? a.situacao}</span>
                                <span>{a.nomeOriginal}</span>
                                {a.mensagem && <span className='text-muted'>{a.mensagem}</span>}
                                {(a.situacao === 'ERRO' || a.situacao === 'DUPLICADO') && (
                                    <button className='btn btn-sm btn-link p-0 ms-auto' disabled={salvando}
                                        onClick={() => executar(() => ModuloBancarioService.descartarNfse(esteiraId, [a.id]), 'Não foi possível descartar.')}>Tirar da lista</button>
                                )}
                            </div>
                        ))}

                        {(visao.totalItens > 0 || filtro.ativo) && (
                            <BarraFiltros filtro={filtro} placeholder='Buscar por número, parte ou arquivo' situacoes={STATUS_FILTRO} />
                        )}
                        {visao.notas.length === 0 && filtro.ativo && <div className='text-muted py-4'>Nenhuma nota com estes filtros.</div>}

                        {visao.notas.length > 0 && (
                            <div className='table-responsive'>
                                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2 mt-3'>
                                    <thead>
                                        <tr className='fw-bold text-muted'>
                                            <th>Nota</th>
                                            <th>Emissão</th>
                                            <th>Serviço</th>
                                            <th>Parte</th>
                                            <th className='text-end'>Bruto</th>
                                            <th className='text-end'>IRRF</th>
                                            <th className='text-end'>ISS</th>
                                            <th className='text-end'>INSS</th>
                                            <th className='text-end'>CSRF</th>
                                            <th className='text-end'>Líquido</th>
                                            <th>Situação</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visao.notas.map(n => (
                                            <tr key={n.id}>
                                                <td className='fs-7'>{n.numero}</td>
                                                <td className='fs-7 text-nowrap'>{n.emissao ? moment(n.emissao).format('DD/MM/YYYY') : '—'}</td>
                                                <td className='fs-7'>{n.direcao === 'TOMADO' ? 'Tomado' : n.direcao === 'PRESTADO' ? 'Prestado' : '—'}</td>
                                                <td className='fs-7'>
                                                    {n.parteNome ?? '—'}
                                                    {n.documentoMascarado && <div className='text-muted fs-8'>{n.documentoMascarado}</div>}
                                                </td>
                                                <td className='text-end text-nowrap fs-7'>{brl(n.bruto)}</td>
                                                <td className='text-end text-nowrap fs-7'>{brl(n.irrf)}</td>
                                                <td className='text-end text-nowrap fs-7'>{brl(n.iss)}</td>
                                                <td className='text-end text-nowrap fs-7'>{brl(n.inss)}</td>
                                                <td className='text-end text-nowrap fs-7'>{brl(n.csrf)}</td>
                                                <td className='text-end text-nowrap fs-7'>{brl(n.liquido)}</td>
                                                <td className='fs-7'>
                                                    <span className={`badge ${STATUS[n.situacao]?.classe ?? 'badge-light'}`}>{STATUS[n.situacao]?.texto ?? n.situacao}</span>
                                                    {n.motivo && <div className='text-muted fs-8'>{n.motivo}</div>}
                                                </td>
                                                <td className='text-end text-nowrap'>
                                                    {podeVincular(n) && (
                                                        <button className='btn btn-sm btn-light me-1' disabled={salvando} onClick={() => abrirVinculo(n)}>
                                                            {n.lancamentoId ? 'Provisão vinculada' : 'Vincular provisão'}
                                                        </button>
                                                    )}
                                                    <button className='btn btn-sm btn-icon btn-light' title={`Abrir ${nomeArquivo(n.arquivoId)}`}
                                                        onClick={() => ModuloBancarioService.baixarNfse(esteiraId, n.arquivoId, nomeArquivo(n.arquivoId))
                                                            .catch((err: any) => Swal.fire({ text: err?.message ?? 'Não foi possível abrir o arquivo.', icon: 'error' }))}>
                                                        <i className='fas fa-file-download'></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <PaginacaoModulo total={visao.totalItens} pagina={filtro.pagina} tamanhoPagina={filtro.tamanhoPagina} onPagina={filtro.setPagina} onTamanho={filtro.setTamanho} />
                    </>
                )}
            </div>

            {vinculo && (
                <Modal show onHide={() => setVinculo(null)} centered size='lg'>
                    <Modal.Header closeButton>
                        <Modal.Title>Provisão da nota {vinculo.nota.numero}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className='text-muted fs-7 mb-3'>Provisões pendentes com o valor bruto da nota ({brl(vinculo.nota.bruto)}). As minutas regeneráveis são refeitas.</div>
                        <BuscaOpcoes placeholder='Buscar por histórico ou conta' mostradas={vinculo.opcoes.length} total={vinculo.total} onBuscar={buscarProvisoes} />
                        {vinculo.opcoes.length === 0 && !vinculo.nota.lancamentoId
                            ? <div className='notice bg-light rounded p-3 fs-7'>Nenhuma provisão pendente tem esse valor.</div>
                            : (
                                <select className='form-select' value={vinculo.escolhida} onChange={e => setVinculo({ ...vinculo, escolhida: e.target.value })}>
                                    <option value=''>Sem vínculo manual</option>
                                    {vinculo.opcoes.map(p => (
                                        <option key={p.id} value={p.id}>{moment(p.data).format('DD/MM/YYYY')} — {p.conta} — {p.historico}</option>
                                    ))}
                                </select>
                            )}
                    </Modal.Body>
                    <Modal.Footer>
                        <button className='btn btn-light' onClick={() => setVinculo(null)}>Cancelar</button>
                        <button className='btn btn-primary' disabled={salvando} onClick={salvarVinculo}>Salvar</button>
                    </Modal.Footer>
                </Modal>
            )}
        </div>
    );
}
